<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted } from 'vue'
  import ContentCard from '../../molecules/ContentCard/ContentCard.vue'
  import type { ContentCarouselProps, ContentCarouselItem } from './types'

  const SWIPE_THRESHOLD = 50

  const props = withDefaults(defineProps<ContentCarouselProps>(), {
    autoplay: false,
    autoplayInterval: 3000,
    showIndicators: true,
    showArrows: true,
    cardVariant: 'default',
  })

  const emit = defineEmits<{
    'item-click': [item: ContentCarouselItem, index: number]
  }>()

  defineSlots<{
    item?: (props: { item: ContentCarouselItem; index: number }) => unknown
  }>()

  const currentIndex = ref(0)
  const dragOffset = ref(0)
  const isDraggingRef = ref(false)
  const isTransitioning = ref(false)
  const isHovered = ref(false)

  let autoplayTimer: ReturnType<typeof setInterval> | null = null
  let pointerStartX = 0
  let isDragging = false

  const trackStyle = computed(() => ({
    transform: `translateX(calc(-${currentIndex.value * 100}% + ${dragOffset.value}px))`,
    transition: isTransitioning.value ? 'transform 300ms ease' : 'none',
  }))

  function goToIndex(index: number) {
    isTransitioning.value = true
    currentIndex.value = index
  }

  function next() {
    goToIndex(
      currentIndex.value < props.items.length - 1 ? currentIndex.value + 1 : 0
    )
  }

  function prev() {
    goToIndex(
      currentIndex.value > 0 ? currentIndex.value - 1 : props.items.length - 1
    )
  }

  const canGoPrev = computed(() => currentIndex.value > 0)
  const canGoNext = computed(() => currentIndex.value < props.items.length - 1)

  // --- Hover (pause autoplay + show arrows) ---
  function onMouseEnter() {
    isHovered.value = true
    stopAutoplay()
  }

  function onMouseLeave() {
    isHovered.value = false
    startAutoplay()
  }

  // --- Pointer (touch + mouse) handling ---
  function onPointerDown(e: PointerEvent) {
    // Ignore right-click
    if (e.button !== 0) return
    isDragging = true
    isDraggingRef.value = true
    pointerStartX = e.clientX
    dragOffset.value = 0
    isTransitioning.value = false
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.setPointerCapture(e.pointerId)
    }
  }

  function onPointerMove(e: PointerEvent) {
    if (!isDragging) return
    dragOffset.value = e.clientX - pointerStartX
  }

  function onPointerUp(e: PointerEvent) {
    if (!isDragging) return
    isDragging = false
    isDraggingRef.value = false
    const delta = pointerStartX - e.clientX

    // Reset drag offset before transitioning so the snap animation is smooth
    dragOffset.value = 0

    if (Math.abs(delta) < SWIPE_THRESHOLD) {
      // Below threshold — snap back to current slide
      isTransitioning.value = true
      return
    }

    if (delta > 0) {
      // Swiped left → next slide
      goToIndex(
        currentIndex.value < props.items.length - 1 ? currentIndex.value + 1 : 0
      )
    } else {
      // Swiped right → previous slide
      goToIndex(
        currentIndex.value > 0 ? currentIndex.value - 1 : props.items.length - 1
      )
    }
  }

  // --- Autoplay ---
  function startAutoplay() {
    if (!props.autoplay) return
    stopAutoplay()
    autoplayTimer = setInterval(() => next(), props.autoplayInterval)
  }

  function stopAutoplay() {
    if (autoplayTimer) {
      clearInterval(autoplayTimer)
      autoplayTimer = null
    }
  }

  onMounted(() => {
    startAutoplay()
  })

  onUnmounted(() => {
    stopAutoplay()
  })
</script>

<template>
  <!-- Slots: #item({ item, index }) — override individual slide content -->
  <div
    data-testid="content-carousel"
    class="group/carousel relative h-full w-full overflow-hidden rounded-lg"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
    @dragstart.prevent
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <!-- Slide track — transform-based positioning with pointer event swiping -->
    <div
      ref="trackRef"
      class="content-carousel-track flex h-full w-full"
      :style="trackStyle"
    >
      <div
        v-for="(item, index) in items"
        :key="item.id"
        class="h-full w-full flex-none"
        :class="{ 'pointer-events-none': isDraggingRef }"
      >
        <slot name="item" :item="item" :index="index">
          <ContentCard
            :variant="cardVariant"
            :thumbnail-url="item.thumbnailUrl"
            :title="item.title"
            :source-name="item.sourceName"
            :source-icon-url="item.sourceIconUrl"
            :time="item.time"
            :show-play-button="item.showPlayButton"
            :inherit-class="showIndicators && items.length > 1 ? 'pb-10' : ''"
            @click="emit('item-click', item, index)"
          />
        </slot>
      </div>
    </div>

    <!-- Navigation arrows — visible on hover -->
    <template v-if="showArrows && items.length > 1">
      <button
        v-show="isHovered && canGoPrev"
        aria-label="Previous slide"
        class="absolute left-1 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center rounded bg-overlay-strong/80 px-1 py-4 shadow-article-card transition-opacity hover:bg-overlay-strong"
        @pointerdown.stop
        @click.stop="prev"
      >
        <svg
          class="h-4 w-4 text-content-on-overlay"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M10 12 6 8l4-4" />
        </svg>
      </button>
      <button
        v-show="isHovered && canGoNext"
        aria-label="Next slide"
        class="absolute right-1 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center rounded bg-overlay-strong/80 px-1 py-4 shadow-article-card transition-opacity hover:bg-overlay-strong"
        @pointerdown.stop
        @click.stop="next"
      >
        <svg
          class="h-4 w-4 text-content-on-overlay"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="m6 4 4 4-4 4" />
        </svg>
      </button>
    </template>

    <!-- Indicators -->
    <div
      v-if="showIndicators && items.length > 1"
      class="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1 rounded-3xl bg-overlay-strong/80 px-2 py-1.5"
    >
      <button
        v-for="(_, index) in items"
        :key="index"
        :aria-label="`Go to slide ${index + 1}`"
        class="h-1.5 rounded-full transition-all duration-150 focus-visible:outline-none"
        :class="
          currentIndex === index
            ? 'w-3 bg-content-on-overlay'
            : 'w-1.5 bg-content-on-overlay/50'
        "
        @pointerdown.stop
        @click="goToIndex(index)"
      />
    </div>
  </div>
</template>

<style scoped>
  .content-carousel-track {
    touch-action: pan-y;
    user-select: none;
    will-change: transform;
  }
</style>
